import { Box, Paper, Typography, useTheme, Stack, Chip } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { LuBox, LuBuilding, LuFolders, LuLayoutTemplate, LuArrowRight } from "react-icons/lu";
import { getOrgTokens } from "../../theme/style/organizationPalette";

const ValidationCenterDashboard = ({ onSelectCategory, stats }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const tok = getOrgTokens(isDark);

    const cards = useMemo(() => [
        {
            id: 'product',
            title: t('Product Validation'),
            description: t('Review and validate product lifecycle models, EPDs, and environmental data.'),
            icon: LuBox,
            accent: '#3b82f6',
            count: stats?.product || 0,
            btnText: t('Review Products')
        },
        {
            id: 'project',
            title: t('Project Validation'),
            description: t('Standardized project configurations and cross-module layout verification.'),
            icon: LuFolders,
            accent: '#10b981',
            count: stats?.project || 0,
            btnText: t('Review Projects')
        },
        {
            id: 'building',
            title: t('Building Validation'),
            description: t('Building archetypes and structural templates for construction audit review.'),
            icon: LuBuilding,
            accent: '#f59e0b',
            count: stats?.building || 0,
            btnText: t('Review Buildings')
        },
        {
            id: 'organization',
            title: t('Organization Validation'),
            description: t('Corporate structures and hierarchy models for organization-level tracking.'),
            icon: LuLayoutTemplate,
            accent: '#8b5cf6',
            count: stats?.organization || 0,
            btnText: t('Review Organizations')
        }
    ], [t, stats]);

    return (
        <Box sx={{ 
            display: 'flex', 
            gap: tok.spacing.metricCardGap, 
            flexWrap: 'wrap', 
            width: '100%',
            mt: 1 
        }}>
            {cards.map((card) => (
                <Box 
                    key={card.id} 
                    sx={{ 
                        flex: '1 1 calc(50% - 12px)', 
                        minWidth: '340px' 
                    }}
                >
                    <Paper
                        onClick={() => onSelectCategory(card.id)}
                        sx={{
                            p: 3,
                            display: 'flex',
                            flexDirection: 'column',
                            height: '100%',
                            minHeight: '180px',
                            borderRadius: tok.radius.card,
                            border: '1px solid',
                            borderColor: tok.border.card,
                            bgcolor: tok.bg.card,
                            boxShadow: tok.shadow.card,
                            transition: 'all 0.3s ease',
                            cursor: 'pointer',
                            position: 'relative',
                            overflow: 'hidden',
                            '&:hover': {
                                borderColor: card.accent,
                                boxShadow: tok.shadow.cardHover,
                                '& .review-btn': {
                                    bgcolor: tok.bg.tabActive,
                                    color: '#fff',
                                    pr: 2.5
                                }
                            }
                        }}
                    >
                        {/* Content Section */}
                        <Stack direction="row" spacing={2} sx={{ mb: 1, width: '100%' }}>
                            <Box
                                className="category-icon-box"
                                sx={{
                                    width: 52,
                                    height: 52,
                                    borderRadius: tok.radius.icon || '12px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    bgcolor: alpha(card.accent, 0.1),
                                    color: card.accent,
                                    boxShadow: `0 4px 12px ${alpha(card.accent, 0.12)}`,
                                    flexShrink: 0,
                                    position: 'relative'
                                }}
                            >
                                <card.icon size={26} />
                            </Box>
                            
                            <Box sx={{ flexGrow: 1 }}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                                    <Typography sx={{ 
                                        fontWeight: 700, 
                                        fontSize: '1.25rem', 
                                        color: tok.text.primary,
                                        fontFamily: tok.font.family,
                                        letterSpacing: '-0.01em',
                                        textAlign: 'left'
                                    }}>
                                        {card.title}
                                    </Typography>
                                    
                                    {card.count > 0 && (
                                        <Chip 
                                            label={`${card.count} ${t("Pending")}`}
                                            size="small"
                                            sx={{ 
                                                bgcolor: alpha(card.accent, 0.1),
                                                color: card.accent,
                                                fontWeight: 700,
                                                fontSize: '0.7rem',
                                                height: '24px',
                                                borderRadius: '6px'
                                            }}
                                        />
                                    )}
                                </Stack>
                                
                                <Typography sx={{ 
                                    color: tok.text.muted, 
                                    fontWeight: 400, 
                                    fontSize: '0.9rem', 
                                    lineHeight: 1.6,
                                    textAlign: 'left',
                                    maxWidth: '95%',
                                    mb: 2
                                }}>
                                    {card.description}
                                </Typography>

                                {/* Action Button at Bottom Right */}
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                                    <Box
                                        className="review-btn"
                                        sx={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            py: 1,
                                            px: 2,
                                            borderRadius: '8px',
                                            bgcolor: alpha(tok.bg.tabActive, 0.1),
                                            color: tok.bg.tabActive,
                                            fontWeight: 700,
                                            fontSize: '0.85rem',
                                            transition: 'all 0.3s ease',
                                            border: `1px solid ${alpha(tok.bg.tabActive, 0.2)}`
                                        }}
                                    >
                                        {card.btnText}
                                        <LuArrowRight size={16} />
                                    </Box>
                                </Box>
                            </Box>
                        </Stack>

                        {/* Subtle Background Decoration */}
                        <Box sx={{
                            position: 'absolute',
                            top: -10,
                            right: -10,
                            width: 80,
                            height: 80,
                            borderRadius: '50%',
                            bgcolor: alpha(card.accent, 0.03),
                            zIndex: 0
                        }} />
                    </Paper>
                </Box>
            ))}
        </Box>
    );
};

export default ValidationCenterDashboard;
